/**
 * tienda-runtime.js — Rellena con datos VIVOS las secciones de tienda (`ofertas`, `novedades`,
 * `top`, `habitual`) que pinta blocks/tienda.js. Se arranca desde core/block-actions.js (el punto
 * único de entrada de la interacción de bloques), así que corre igual en el editor, en «Ver sitio»,
 * en la SPA en vivo y en el sitio publicado.
 *
 * DE DÓNDE SALEN LOS DATOS
 *   · Editor y «Ver sitio» (`BUILDER_BASE` sin `IS_PUBLIC`): la API del builder,
 *     `{BUILDER_BASE}/api/escaparate/` (misma sesión del ERP). Quien edita no es un cliente de la
 *     tienda, así que «Ver como» elige qué se enseña: `con` = lo que podría ver un cliente
 *     (`vista=muestra`), `sin` = lo de un visitante sin sesión (`vista=general` o el aviso) y
 *     `vacio` = el marcador de «sin contenido». Es estado de VISTA PREVIA: vive aquí, no en el
 *     bloque, y no se publica (como la diapositiva en edición del carrusel, invariante nº20).
 *   · Sitio publicado y SPA en vivo: la TIENDA, `{shop}/site_tienda/escaparate/`, con
 *     `credentials:'include'`. La tienda vive en OTRO host; si comparte dominio con la web la
 *     cookie de sesión del cliente viaja y la tienda contesta con SUS ofertas y precios. Sin
 *     cabeceras propias a propósito: una petición «simple» no paga preflight CORS.
 *
 * DOMINIOS DISTINTOS (web en `www.cliente.es`, tienda en otro dominio): la cookie no viaja. El botón
 * del aviso lleva al PUENTE de la tienda (`/site_tienda/puente/?volver=<esta página>`): si el cliente
 * ya tenía sesión vuelve al instante, y si no, entra y vuelve. Vuelve con un token firmado en el
 * fragmento (`#n3i_tk=…`, que no llega a ningún servidor ni a ningún Referer); se guarda por origen
 * de la tienda, se quita de la URL y viaja en el CUERPO de un POST de formulario (petición simple,
 * sin preflight). El token es de solo lectura: solo lo acepta el endpoint del escaparate.
 *
 * Qué se enseña con la respuesta lo decide `decidirEstado` (tienda-comun.js, puro y testeado).
 * Nunca se mete en el DOM un dato del servidor sin pasar por las funciones de tarjeta, que escapan.
 */
import {
    TIENDA_TIPOS, TIENDA_MOTIVOS, decidirEstado, setTiendaShop,
    tarjetaOferta, tarjetaProducto, bannerCuerpo, bannerFoto, bannerResto,
} from '../blocks/tienda-comun.js';

const SELECTOR = 'section[data-tienda]';
const TIMEOUT_MS = 8000;
const CACHE_EDITOR_MS = 60000;   // el editor re-pinta TODA la página en cada cambio: sin esto,
                                 // cada tecla en otro bloque volvería a pedir los datos.

const _verComo = new Map();      // id de sección → 'con' | 'sin' | 'vacio' (solo vista previa)
const _cache = new Map();        // url → { t, data } (solo editor)
const _vuelo = new Map();        // url → Promise (peticiones iguales en curso, cualquier superficie)
let _estadoPromesa = null;

const esVistaDelEditor = () =>
    typeof window !== 'undefined' && !!window.BUILDER_BASE && !window.IS_PUBLIC;

function leerParams(section) {
    try {
        const p = JSON.parse(section.getAttribute('data-tienda') || '{}');
        return (p && typeof p === 'object' && TIENDA_TIPOS[p.kind]) ? p : null;
    } catch (e) {
        return null;
    }
}

/** GET JSON que nunca lanza: null si falla, tarda o no es JSON. */
function pedir(url, opciones) {
    if (_vuelo.has(url)) return _vuelo.get(url);
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const tope = ctrl ? setTimeout(() => ctrl.abort(), TIMEOUT_MS) : 0;
    // `url` es también la clave de «la misma petición en curso»: un sufijo `#…` (que fetch no envía)
    // separa la variante con token de la anónima.
    const prom = fetch(url.split('#')[0], Object.assign({ signal: ctrl ? ctrl.signal : undefined }, opciones || {}))
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
        .then((data) => {
            clearTimeout(tope);
            _vuelo.delete(url);
            return data && typeof data === 'object' ? data : null;
        });
    _vuelo.set(url, prom);
    return prom;
}

// ── Token del puente (dominios distintos) ────────────────────────────────────────────

const CLAVE_TOKEN = 'n3i_tk:';
// Los tokens de `django.core.signing`: base64 «url-safe» y `:` como separador.
const RE_TOKEN = /(?:^#|&)n3i_tk=([A-Za-z0-9_\-:]{20,600})(?=&|$)/;

function origen(url) {
    try { return new URL(url).origin; } catch (e) { return ''; }
}

// localStorage puede no estar (modo privado estricto, iframes sin permiso): todo falla en blando.
function tokenDe(shop) {
    try { return window.localStorage.getItem(CLAVE_TOKEN + origen(shop)) || ''; } catch (e) { return ''; }
}

function guardarToken(shop, token) {
    try { window.localStorage.setItem(CLAVE_TOKEN + origen(shop), token); } catch (e) { /* sin almacén */ }
}

function borrarToken(shop) {
    try { window.localStorage.removeItem(CLAVE_TOKEN + origen(shop)); } catch (e) { /* sin almacén */ }
}

/**
 * El puente devuelve al cliente con `#n3i_tk=<token>`: se guarda para cada tienda de la página y se
 * QUITA de la barra de direcciones en el acto (nadie lo copia al compartir el enlace).
 */
function capturarTokenDelFragmento() {
    const m = RE_TOKEN.exec(window.location.hash || '');
    if (!m) return;
    const tiendas = new Set();
    document.querySelectorAll(SELECTOR).forEach((s) => {
        const p = leerParams(s);
        if (p && /^https?:\/\//i.test(p.shop || '')) tiendas.add(p.shop);
    });
    tiendas.forEach((shop) => guardarToken(shop, m[1]));
    const resto = window.location.hash.replace(/^#/, '').split('&')
        .filter((parte) => parte && parte.indexOf('n3i_tk=') !== 0).join('&');
    try {
        window.history.replaceState(window.history.state, '',
            window.location.pathname + window.location.search + (resto ? '#' + resto : ''));
    } catch (e) { /* sin History API: el fragmento se queda, no pasa nada más */ }
}

/** El botón del aviso: al puente de la tienda, que devuelve aquí (sin el fragmento). */
function urlDelPuente(shop) {
    return shop + '/site_tienda/puente/?volver=' + encodeURIComponent(window.location.href.split('#')[0]);
}

function qs(obj) {
    return Object.keys(obj).filter((k) => obj[k] !== '' && obj[k] != null)
        .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(obj[k])).join('&');
}

/**
 * Estado de la tienda para el editor (`api/escaparate/estado/`): qué pestañas están disponibles y
 * la URL de la tienda, que se fija como estado de render para que las secciones la horneen. Una
 * sola petición por carga del editor; la reutilizan el panel «Tienda» y la publicación.
 */
export function cargarEstadoTienda(forzar) {
    if (!esVistaDelEditor()) return Promise.resolve(null);
    if (_estadoPromesa && !forzar) return _estadoPromesa;
    _estadoPromesa = pedir(window.BUILDER_BASE + '/api/escaparate/estado/', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((data) => {
            if (data) setTiendaShop(data.shop || '');
            else _estadoPromesa = null;   // falló: que el próximo intento vuelva a preguntar
            return data;
        });
    return _estadoPromesa;
}

function pedirEditor(params, vista) {
    const url = window.BUILDER_BASE + '/api/escaparate/?' +
        qs({ kind: params.kind, count: params.count, order: params.order, vista: vista });
    const c = _cache.get(url);
    if (c && Date.now() - c.t < CACHE_EDITOR_MS) return Promise.resolve(c.data);
    return pedir(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then((data) => {
        if (data) _cache.set(url, { t: Date.now(), data: data });
        return data;
    });
}

function pedirTienda(params) {
    if (!/^https?:\/\//i.test(params.shop || '')) return Promise.resolve(null);
    const datos = { kind: params.kind, count: params.count, order: params.order };
    const url = params.shop + '/site_tienda/escaparate/?' + qs(datos);
    const tk = tokenDe(params.shop);
    if (!tk) return pedir(url, { credentials: 'include', mode: 'cors' });
    // Con token: POST de formulario (petición simple) y el token en el CUERPO, nunca en la URL.
    const cuerpo = new URLSearchParams(Object.assign({ tk: tk }, datos));
    return pedir(url + '#tk', { method: 'POST', credentials: 'include', mode: 'cors', body: cuerpo })
        .then((data) => {
            if (data && data.token_invalid) borrarToken(params.shop);
            return data;
        });
}

// ── Pintado ────────────────────────────────────────────────────────────────────────

function slot(section, nombre) {
    return section.querySelector('[data-ts-slot="' + nombre + '"]');
}

function pintarItems(section, params, items) {
    const ctx = {
        labels: params.labels || {}, lang: params.lang || document.documentElement.lang || 'es',
        prices: params.prices, cart: params.cart, tag: params.tag || '',
    };
    const lista = items.slice(0, params.count);
    if (params.variant === 'v3') {
        const foto = slot(section, 'bfoto');
        const cuerpo = slot(section, 'bcuerpo');
        const resto = slot(section, 'bresto');
        if (foto) foto.innerHTML = bannerFoto(lista[0]);
        if (cuerpo) cuerpo.innerHTML = bannerCuerpo(lista[0], ctx);
        if (resto) resto.innerHTML = bannerResto(lista.slice(1), ctx);
        return;
    }
    const hueco = slot(section, 'items');
    if (!hueco) return;
    hueco.innerHTML = lista.map((it) =>
        (params.kind === 'ofertas' ? tarjetaOferta(it, ctx) : tarjetaProducto(it, ctx))).join('');
}

function fijarEstado(section, estado, extra) {
    section.setAttribute('data-ts-estado', estado);
    if (extra && extra.muestra) section.setAttribute('data-ts-muestra', '1');
    else section.removeAttribute('data-ts-muestra');
    if (estado === 'off') {
        const motivo = section.querySelector('[data-ts-motivo]');
        if (motivo) motivo.textContent = TIENDA_MOTIVOS[(extra && extra.reason) || 'sinTienda'] || TIENDA_MOTIVOS.sinTienda;
    }
}

function marcarVerComo(section, ver) {
    section.querySelectorAll('.ts-tb-btn[data-ts-set="ver"]').forEach((b) => {
        b.classList.toggle('is-active', b.getAttribute('data-ts-val') === ver);
    });
}

function enlaceDeAcceso(section, params) {
    const a = section.querySelector('[data-ts-login]');
    if (!a) return;
    if (esVistaDelEditor() || !/^https?:\/\//i.test(params.shop || '')) a.setAttribute('href', '#');
    else a.setAttribute('href', urlDelPuente(params.shop));
}

async function hidratarEditor(section, params) {
    const cfg = TIENDA_TIPOS[params.kind];
    const ver = _verComo.get(section.id) || 'con';
    marcarVerComo(section, ver);
    enlaceDeAcceso(section, params);
    if (ver === 'vacio') { fijarEstado(section, 'vacio'); return; }
    if (ver === 'sin' && (params.visibility === 'sesion' || cfg.soloSesion)) {
        fijarEstado(section, params.fallback === 'aviso' ? 'gate' : 'oculto');
        return;
    }
    fijarEstado(section, 'cargando');
    const data = await pedirEditor(params, ver === 'sin' ? 'general' : 'muestra');
    if (!section.isConnected) return;
    if (!data) { fijarEstado(section, 'off', { reason: 'error' }); return; }
    if (data.configured === false || data.on === false) {
        fijarEstado(section, 'off', { reason: data.reason || 'sinTienda' });
        return;
    }
    // Tienda privada vista sin sesión: lo mismo que verá ese visitante (decidirEstado).
    if (data.private) { fijarEstado(section, params.fallback === 'aviso' ? 'gate' : 'oculto'); return; }
    const items = Array.isArray(data.items) ? data.items : [];
    if (!items.length) { fijarEstado(section, 'vacio'); return; }
    pintarItems(section, params, items);
    fijarEstado(section, 'items', { muestra: !!data.sample });
}

async function hidratarPublicado(section, params) {
    enlaceDeAcceso(section, params);
    const resp = await pedirTienda(params);
    if (!section.isConnected) return;
    const estado = decidirEstado(params, resp);
    if (estado === 'items') pintarItems(section, params, resp.items);
    fijarEstado(section, estado);
}

function hidratar(section) {
    const params = leerParams(section);
    if (!params) return;
    section.setAttribute('data-ts-ready', '1');
    if (esVistaDelEditor()) hidratarEditor(section, params);
    else hidratarPublicado(section, params);
}

/**
 * Pinta las secciones de tienda aún sin datos. Idempotente: el editor la llama tras cada
 * `renderPage` (son elementos NUEVOS) y una sección ya pintada no se vuelve a pedir.
 */
export function hydrateTienda(root) {
    if (typeof document === 'undefined') return;
    const base = root || document;
    const pendientes = base.querySelectorAll(SELECTOR + ':not([data-ts-ready])');
    if (!pendientes.length) return;
    if (esVistaDelEditor()) cargarEstadoTienda();
    // En lo publicado se piden al acercarse a la pantalla: una sección del pie no gasta una
    // petición a quien nunca baja hasta ella.
    if (!esVistaDelEditor() && typeof IntersectionObserver !== 'undefined') {
        // Se observa el CONTENEDOR de la sección: una sección «solo con sesión» nace oculta (sin
        // caja) y el observador no la vería nunca. Varias secciones pueden compartirlo.
        const porObjetivo = new Map();
        const io = new IntersectionObserver((entradas) => {
            entradas.forEach((e) => {
                if (!e.isIntersecting) return;
                io.unobserve(e.target);
                (porObjetivo.get(e.target) || []).forEach(hidratar);
                porObjetivo.delete(e.target);
            });
        }, { rootMargin: '400px 0px' });
        pendientes.forEach((s) => {
            s.setAttribute('data-ts-ready', '1');
            const objetivo = s.parentElement || s;
            if (!porObjetivo.has(objetivo)) { porObjetivo.set(objetivo, []); io.observe(objetivo); }
            porObjetivo.get(objetivo).push(s);
        });
        return;
    }
    pendientes.forEach(hidratar);
}

/** «Ver como» de la barra del editor: cambia la vista previa de UNA sección y la vuelve a pintar. */
export function setVerComo(section, ver) {
    if (!section || (ver !== 'con' && ver !== 'sin' && ver !== 'vacio')) return;
    _verComo.set(section.id, ver);
    section.removeAttribute('data-ts-ready');
    hidratar(section);
}

/** Flechas del carrusel (v2): desplazan la pista 320 px. Delegado: sin `on*` inline. */
export function initTienda() {
    if (typeof window === 'undefined' || window.__n3iTienda) return;
    window.__n3iTienda = true;
    document.addEventListener('click', (e) => {
        const flecha = e.target.closest && e.target.closest('.ts-arrow');
        if (!flecha) return;
        const section = flecha.closest(SELECTOR);
        const pista = section && section.querySelector('[data-ts-track]');
        if (!pista) return;
        e.preventDefault();
        const dir = flecha.getAttribute('data-ts-dir') === '-1' ? -1 : 1;
        pista.scrollBy({ left: dir * 320, behavior: 'smooth' });
    });
    if (!esVistaDelEditor()) capturarTokenDelFragmento();
    // En el editor una sección se vuelve a crear de dos maneras: `renderPage` (rehace los hijos de
    // #resultado; lo ve el observador de block-actions.js, que espera a un frame) y
    // `repintarBloque` (cambia UN bloque por dentro: la vista previa del menú de variantes, el
    // redimensionado), que ese observador no ve. Este mira el subárbol entero, pero solo reacciona
    // si entra un ELEMENTO con una sección de tienda sin pintar: teclear añade nodos de texto, que
    // se descartan sin buscar nada.
    if (esVistaDelEditor() && typeof MutationObserver !== 'undefined') {
        let pendiente = false;
        new MutationObserver((ms) => {
            if (pendiente) return;
            const hay = ms.some((m) => Array.prototype.some.call(m.addedNodes, (n) =>
                n.nodeType === 1 && (n.matches(SELECTOR) || !!n.querySelector(SELECTOR + ':not([data-ts-ready])'))));
            if (!hay) return;
            pendiente = true;
            setTimeout(() => { pendiente = false; hydrateTienda(); }, 0);
        }).observe(document.getElementById('resultado') || document.body, { childList: true, subtree: true });
    }
    hydrateTienda();
}
